import { GradeAnswerDocument } from "@/generated/graphql";
import { AIGradingModeEnum, type VideoCheckpoint } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { HiX } from "react-icons/hi";
import { HiSparkles } from "react-icons/hi";
import { IoSend } from "react-icons/io5";

interface CheckpointResult {
  score: number;
  studentAnswer: string;
  explanation: string;
}

interface CheckpointRowProps {
  checkpoint: VideoCheckpoint;
  index: number;
}

function CheckpointRow({ checkpoint, index }: CheckpointRowProps) {
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState<CheckpointResult | null>(null);

  const [gradeAnswer, { loading }] = useMutation(GradeAnswerDocument);

  const handleTest = async () => {
    if (!answer.trim() || !checkpoint.question) return;

    const res = await gradeAnswer({
      variables: {
        studentAnswer: answer.trim(),
        correctAnswer: checkpoint.answer ?? "none",
        question: checkpoint.question,
        mode: checkpoint.gradingMode,
      },
    });

    const data = res.data?.gradeAnswer;
    if (!data) return;

    setResult({
      score: data.score,
      studentAnswer: data.studentAnswer,
      explanation: data.explanation,
    });
  };

  const scoreColor =
    result === null
      ? ""
      : result.score >= 70
        ? "text-green-600"
        : result.score >= 40
          ? "text-yellow-600"
          : "text-red-500";

  return (
    <div className="flex flex-col gap-2 p-4 rounded-2xl bg-white/60 border border-white/80 shadow-sm">
      {/* Checkpoint header */}
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
          {index + 1}
        </span>
        <p className="text-xs font-semibold text-gray-700 flex-1 leading-snug">
          {checkpoint.question ?? <span className="text-gray-400 italic">No question</span>}
        </p>
        {result !== null && (
          <span className={`text-sm font-bold shrink-0 ${scoreColor}`}>{result.score}%</span>
        )}
      </div>

      {/* Correct answer reference */}
      {checkpoint.gradingMode !== AIGradingModeEnum.Values.contextMode && checkpoint.answer && (
        <div className="flex items-center gap-1.5 text-[10px]">
          <span className="text-gray-400 shrink-0">
            {checkpoint.gradingMode === AIGradingModeEnum.Values.grammarMode
              ? "Template:"
              : "Keyword:"}
          </span>
          <span className="font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-lg">
            {checkpoint.answer}
          </span>
        </div>
      )}

      {/* Input + button */}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Type your answer..."
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleTest()}
          className="flex-1 px-3 py-2 text-xs border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder:text-gray-400 text-black transition-all"
          disabled={loading}
        />
        <button
          type="button"
          onClick={handleTest}
          disabled={!answer.trim() || loading || !checkpoint.question}
          className="px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:shadow-md hover:scale-105 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {loading ? (
            <span className="flex gap-0.5">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:0ms]" />
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:300ms]" />
            </span>
          ) : (
            <IoSend className="text-sm" />
          )}
        </button>
      </div>

      {/* Result */}
      {result !== null && (
        <div
          className={`text-[11px] rounded-xl px-3 py-2 font-medium ${result.score >= 70 ? "bg-green-50 text-green-700 border border-green-200" : result.score >= 40 ? "bg-yellow-50 text-yellow-700 border border-yellow-200" : "bg-red-50 text-red-600 border border-red-200"}`}
        >
          <span className="font-semibold">Explanation:</span> {result.explanation}
        </div>
      )}
    </div>
  );
}

export function GrammarResponseGradingTestModal({
  isOpen,
  onClose,
  checkpoints,
}: {
  isOpen: boolean;
  onClose: () => void;
  checkpoints: VideoCheckpoint[];
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-2xl shadow-2xl flex flex-col p-6 z-10 border border-white/50"
        style={{ width: 560, maxHeight: "80vh" }}
      >
        {/* Decorative orbs */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-blue-400/30 to-purple-400/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-purple-400/30 to-pink-400/30 rounded-full blur-3xl pointer-events-none" />

        {/* Header */}
        <div className="flex justify-between items-center mb-6 relative shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <HiSparkles className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Grading Test
              </h2>
              <p className="text-xs text-gray-500">Test each checkpoint's scoring</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center bg-white hover:bg-red-50 text-red-500 rounded-xl hover:shadow-md transition-all duration-200 border border-gray-200"
          >
            <HiX className="text-xl" />
          </button>
        </div>

        {/* Checkpoint list */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3">
          {checkpoints.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <p className="text-sm text-gray-400 font-medium">No checkpoints to test</p>
            </div>
          ) : (
            checkpoints.map((checkpoint, index) => (
              <CheckpointRow
                key={`${checkpoint.timestamp}-${index}`}
                checkpoint={checkpoint}
                index={index}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
